import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { subscriptionPlanApi } from '../services/api';
import { 
  NavArrowLeft, User as UserIcon, CreditCard, DatabaseBackup, 
  Page, WarningTriangle, CheckCircle, Xmark, RefreshDouble 
} from 'iconoir-react';
import '../admin.css';

interface UserDetailData {
  user: {
    id: number;
    name: string;
    email: string;
    company_name: string | null;
    billing_address: string | null;
    billing_city: string | null;
    billing_postal_code: string | null;
    billing_country: string | null;
    vat_number: string | null;
    payment_method: string | null;
    created_at: string;
    last_login_at: string | null;
    login_count: number;
    last_activity_at: string | null;
  };
  subscription: {
    id: number;
    billing_period: string;
    status: string;
    current_period_start: string;
    current_period_end: string;
    plan?: {
      name: string;
      display_name: string;
    };
  } | null;
  plan?: {
    id: number;
    name: string;
    display_name: string;
    price_monthly: number;
    price_yearly: number;
    has_ads: boolean;
    max_boards: number | null;
    max_cards_per_board: number | null;
    max_storage_mb: number | null;
  } | null;
  advertising?: {
    interests: string[] | null;
    pcs_level_1: string | null;
    pcs_level_2: string | null;
    pcs_level_3: string | null;
    pcs_level_4: string | null;
    age: number | null;
    gender: string | null;
  };
  usage: {
    owned_boards_count: number;
    invited_editors_count: number;
    invited_visitors_count: number;
    storage_used_gb: string | number;
    last_calculated_at: string;
  };
  cost: {
    base_price: number;
    invited_editors_cost: number;
    invited_editors_count: number;
    storage_cost: number;
    storage_used_gb: number;
    storage_extra_gb: number;
    total: number;
    monthly_equivalent: number;
    billing_period: string;
  };
  invoices: Array<{
    id: number;
    invoice_number: string;
    amount: number;
    status: string;
    due_date: string;
    paid_at: string | null;
    created_at: string;
  }>;
  boards: Array<{
    id: number;
    title: string;
    created_at: string;
  }>;
}

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'info' | 'billing' | 'usage' | 'invoices' | 'boards' | 'engagement' | 'advertising' | 'plan'>('info');

  useEffect(() => {
    loadUserDetails();
  }, [userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/users/${userId}`);
      setData(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir suspendre cet utilisateur ?')) return;
    
    try {
      await api.post(`/admin/users/${userId}/suspend`);
      await loadUserDetails();
      alert('Utilisateur suspendu avec succès');
    } catch (error) {
      console.error('Erreur lors de la suspension:', error);
      alert('Erreur lors de la suspension');
    }
  };

  const handleReactivate = async () => {
    try {
      await api.post(`/admin/users/${userId}/reactivate`);
      await loadUserDetails();
      alert('Utilisateur réactivé avec succès');
    } catch (error) {
      console.error('Erreur lors de la réactivation:', error);
      alert('Erreur lors de la réactivation');
    }
  };

  const handleAssignGoldenTicket = async () => {
    if (!window.confirm('Attribuer le Golden Ticket à cet utilisateur ?')) return;

    try {
      await subscriptionPlanApi.assignGoldenTicket(Number(userId));
      await loadUserDetails();
      alert('Golden Ticket assigné avec succès');
    } catch (error) {
      console.error('Erreur lors de l\'attribution du Golden Ticket:', error);
      alert('Erreur lors de l\'attribution du Golden Ticket');
    }
  };

  const handleRevokeGoldenTicket = async () => {
    if (!window.confirm('Révoquer le Golden Ticket de cet utilisateur ?')) return;

    try {
      await subscriptionPlanApi.revokeGoldenTicket(Number(userId));
      await loadUserDetails();
      alert('Golden Ticket révoqué avec succès');
    } catch (error: any) {
      console.error('Erreur lors de la révocation du Golden Ticket:', error);
      alert(error.response?.data?.message || 'Erreur lors de la révocation du Golden Ticket');
    }
  };

  const handleRecalculateUsage = async () => {
    try {
      const response = await api.post(`/admin/users/${userId}/recalculate-usage`);
      setData(prev => prev ? { ...prev, usage: response.data.usage, cost: response.data.cost } : null);
      alert('Utilisation recalculée avec succès');
    } catch (error) {
      console.error('Erreur lors du recalcul:', error);
      alert('Erreur lors du recalcul');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status: string) => {
    const badges: { [key: string]: { label: string; color: string } } = {
      active: { label: 'Actif', color: '#00bf7e' },
      suspended: { label: 'Suspendu', color: '#e74c3c' },
      cancelled: { label: 'Annulé', color: '#95a5a6' },
      pending: { label: 'En attente', color: '#f39c12' },
      paid: { label: 'Payée', color: '#00bf7e' },
      failed: { label: 'Échouée', color: '#e74c3c' },
    };
    return badges[status] || { label: status, color: '#95a5a6' };
  };

  if (loading || !data) {
    return <div className="admin-loading">Chargement...</div>;
  }

  const { user, subscription, usage, cost, invoices, boards, advertising, plan } = data;

  const handleDeleteAdvertisingData = async () => {
    if (!window.confirm('Supprimer définitivement les données publicitaires de cet utilisateur ?')) return;
    try {
      await api.delete(`/admin/users/${user.id}/advertising-data`);
      alert('Données supprimées.');
      loadUserDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression.');
    }
  };

  return (
    <div className="user-detail">
      {/* Header */}
      <div className="user-detail-header">
        <button onClick={() => navigate('/admin/saas')} className="btn-back">
          <NavArrowLeft width={20} height={20} />
          Retour
        </button>
        
        <div className="user-header-info">
          <h1>{user.name}</h1>
          <p className="user-email">{user.email}</p>
          {user.company_name && <p className="user-company">{user.company_name}</p>}
        </div>

        <div className="user-header-actions">
          <button className="btn btn-secondary" onClick={handleRecalculateUsage}>
            <RefreshDouble width={18} height={18} strokeWidth={2} />
            Recalculer
          </button>
          {subscription?.status === 'active' ? (
            <button className="btn btn-danger" onClick={handleSuspend}>
              <Xmark width={18} height={18} strokeWidth={2} />
              Suspendre
            </button>
          ) : (
            <button className="btn btn-success" onClick={handleReactivate}>
              <CheckCircle width={18} height={18} strokeWidth={2} />
              Réactiver
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="user-detail-tabs">
        <button
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <UserIcon width={18} height={18} strokeWidth={2} />
          Informations
        </button>
        <button
          className={`tab ${activeTab === 'billing' ? 'active' : ''}`}
          onClick={() => setActiveTab('billing')}
        >
          <CreditCard width={18} height={18} strokeWidth={2} />
          Facturation
        </button>
        <button
          className={`tab ${activeTab === 'usage' ? 'active' : ''}`}
          onClick={() => setActiveTab('usage')}
        >
          <DatabaseBackup width={18} height={18} strokeWidth={2} />
          Utilisation
        </button>
        <button
          className={`tab ${activeTab === 'invoices' ? 'active' : ''}`}
          onClick={() => setActiveTab('invoices')}
        >
          <Page width={18} height={18} strokeWidth={2} />
          Factures ({invoices.length})
        </button>
        <button
          className={`tab ${activeTab === 'boards' ? 'active' : ''}`}
          onClick={() => setActiveTab('boards')}
        >
          <Page width={18} height={18} strokeWidth={2} />
          Tableaux ({boards.length})
        </button>
        <button
          className={`tab ${activeTab === 'engagement' ? 'active' : ''}`}
          onClick={() => setActiveTab('engagement')}
        >
          <DatabaseBackup width={18} height={18} strokeWidth={2} />
          Engagement
        </button>
        <button
          className={`tab ${activeTab === 'advertising' ? 'active' : ''}`}
          onClick={() => setActiveTab('advertising')}
        >
          <Page width={18} height={18} strokeWidth={2} />
          Données pub
        </button>
        <button
          className={`tab ${activeTab === 'plan' ? 'active' : ''}`}
          onClick={() => setActiveTab('plan')}
        >
          <CreditCard width={18} height={18} strokeWidth={2} />
          Plan actuel
        </button>
      </div>

      {/* Tab Content */}
      <div className="user-detail-content">
        {activeTab === 'info' && (
          <div className="tab-panel">
            <div className="info-grid">
              <div className="info-card">
                <h3>Informations générales</h3>
                <div className="info-row">
                  <span className="info-label">Nom :</span>
                  <span className="info-value">{user.name}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Email :</span>
                  <span className="info-value">{user.email}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Inscrit le :</span>
                  <span className="info-value">{formatDate(user.created_at)}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Statut :</span>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusBadge(subscription?.status || 'none').color }}
                  >
                    {getStatusBadge(subscription?.status || 'none').label}
                  </span>
                </div>
              </div>

              {subscription && (
                <div className="info-card">
                  <h3>
                    Abonnement
                    {subscription.plan?.name === 'golden_ticket' && (
                      <span className="status-badge" style={{ backgroundColor: '#f5a623', marginLeft: '8px' }}>
                        Golden Ticket
                      </span>
                    )}
                  </h3>
                  {subscription.plan && (
                    <div className="info-row">
                      <span className="info-label">Plan :</span>
                      <span className="info-value">{subscription.plan.display_name}</span>
                    </div>
                  )}
                  <div className="info-row">
                    <span className="info-label">Période :</span>
                    <span className="info-value">
                      {subscription.billing_period === 'yearly' ? 'Annuel' : 'Mensuel'}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Début période :</span>
                    <span className="info-value">{formatDate(subscription.current_period_start)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Fin période :</span>
                    <span className="info-value">{formatDate(subscription.current_period_end)}</span>
                  </div>
                  <div className="info-row" style={{ marginTop: '12px', gap: '8px' }}>
                    {subscription.plan?.name !== 'golden_ticket' ? (
                      <button className="btn btn-secondary" onClick={handleAssignGoldenTicket}>
                        Attribuer Golden Ticket
                      </button>
                    ) : (
                      <button className="btn btn-danger" onClick={handleRevokeGoldenTicket}>
                        Révoquer Golden Ticket
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="tab-panel">
            <div className="info-card">
              <h3>Adresse de facturation</h3>
              <div className="info-row">
                <span className="info-label">Société :</span>
                <span className="info-value">{user.company_name || '-'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Adresse :</span>
                <span className="info-value">{user.billing_address || '-'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Ville :</span>
                <span className="info-value">
                  {user.billing_postal_code} {user.billing_city || '-'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Pays :</span>
                <span className="info-value">{user.billing_country || '-'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">N° TVA :</span>
                <span className="info-value">{user.vat_number || '-'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Moyen de paiement :</span>
                <span className="info-value">{user.payment_method || '-'}</span>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="tab-panel">
            <div className="usage-summary">
              <div className="usage-card">
                <h3>Utilisation actuelle</h3>
                <div className="usage-stats">
                  <div className="usage-stat">
                    <span className="usage-label">Tableaux possédés :</span>
                    <span className="usage-value">{usage.owned_boards_count}</span>
                  </div>
                  <div className="usage-stat">
                    <span className="usage-label">Invités éditeurs/admins :</span>
                    <span className="usage-value">{usage.invited_editors_count}</span>
                  </div>
                  <div className="usage-stat">
                    <span className="usage-label">Invités visiteurs :</span>
                    <span className="usage-value">{usage.invited_visitors_count}</span>
                  </div>
                  <div className="usage-stat">
                    <span className="usage-label">Stockage utilisé :</span>
                    <span className="usage-value">{Number(usage.storage_used_gb).toFixed(2)} Go</span>
                  </div>
                  <div className="usage-stat">
                    <span className="usage-label">Dernière mise à jour :</span>
                    <span className="usage-value">{formatDate(usage.last_calculated_at)}</span>
                  </div>
                </div>
              </div>

              <div className="usage-card">
                <h3>Calcul des coûts</h3>
                <div className="cost-breakdown">
                  <div className="cost-row">
                    <span>Accès de base ({cost.billing_period === 'yearly' ? 'annuel' : 'mensuel'}) :</span>
                    <span>{formatCurrency(cost.base_price)}</span>
                  </div>
                  <div className="cost-row">
                    <span>Invités éditeurs ({cost.invited_editors_count}) :</span>
                    <span>{formatCurrency(cost.invited_editors_cost)}</span>
                  </div>
                  <div className="cost-row">
                    <span>Stockage supplémentaire ({cost.storage_extra_gb} Go) :</span>
                    <span>{formatCurrency(cost.storage_cost)}</span>
                  </div>
                  <div className="cost-row cost-total">
                    <span>Total {cost.billing_period === 'yearly' ? 'annuel' : 'mensuel'} :</span>
                    <span>{formatCurrency(cost.total)}</span>
                  </div>
                  {cost.billing_period === 'yearly' && (
                    <div className="cost-row cost-equivalent">
                      <span>Équivalent mensuel :</span>
                      <span>{formatCurrency(cost.monthly_equivalent)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="tab-panel">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>N° Facture</th>
                  <th>Montant</th>
                  <th>Statut</th>
                  <th>Date d'échéance</th>
                  <th>Payée le</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const statusBadge = getStatusBadge(invoice.status);
                  return (
                    <tr key={invoice.id}>
                      <td>{invoice.invoice_number}</td>
                      <td>{formatCurrency(invoice.amount)}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={{ backgroundColor: statusBadge.color }}
                        >
                          {statusBadge.label}
                        </span>
                      </td>
                      <td>{formatDate(invoice.due_date)}</td>
                      <td>{invoice.paid_at ? formatDate(invoice.paid_at) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'boards' && (
          <div className="tab-panel">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Créé le</th>
                </tr>
              </thead>
              <tbody>
                {boards.map((board) => (
                  <tr key={board.id}>
                    <td>{board.title}</td>
                    <td>{formatDate(board.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'advertising' && (
          <div className="tab-panel">
            <div className="info-card">
              <h3>Données publicitaires</h3>
              {advertising && (advertising.interests || advertising.age) ? (
                <>
                  <div className="info-row">
                    <span className="info-label">Centres d'intérêt :</span>
                    <span className="info-value">{advertising.interests?.join(', ') || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Catégorie socio-pro (niveau 1) :</span>
                    <span className="info-value">{advertising.pcs_level_1 || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Catégorie socio-pro (niveau 2) :</span>
                    <span className="info-value">{advertising.pcs_level_2 || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Âge :</span>
                    <span className="info-value">{advertising.age || '—'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Genre :</span>
                    <span className="info-value">{advertising.gender || '—'}</span>
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <button className="btn btn-danger" onClick={handleDeleteAdvertisingData}>
                      Supprimer les données publicitaires
                    </button>
                    {subscription?.status === 'active' && plan?.has_ads && (
                      <p style={{ color: '#e74c3c', fontSize: '13px', marginTop: '8px' }}>
                        Impossible de supprimer : l'utilisateur a un plan gratuit actif.
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <p style={{ color: '#666' }}>Aucune donnée publicitaire enregistrée.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'plan' && (
          <div className="tab-panel">
            <div className="info-card">
              <h3>Plan actuel</h3>
              {plan ? (
                <>
                  <div className="info-row">
                    <span className="info-label">Nom du plan :</span>
                    <span className="info-value">{plan.display_name}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Type :</span>
                    <span className="info-value">{plan.has_ads ? 'Gratuit (avec publicités)' : 'Premium'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Prix mensuel :</span>
                    <span className="info-value">{plan.price_monthly} €</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Prix annuel :</span>
                    <span className="info-value">{plan.price_yearly} €</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Tableaux max :</span>
                    <span className="info-value">{plan.max_boards ?? 'Illimité'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Cartes max / tableau :</span>
                    <span className="info-value">{plan.max_cards_per_board ?? 'Illimité'}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Stockage max :</span>
                    <span className="info-value">{plan.max_storage_mb ? `${plan.max_storage_mb} Mo` : 'Illimité'}</span>
                  </div>
                </>
              ) : (
                <p style={{ color: '#666' }}>Aucun plan actif.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'engagement' && (
          <div className="tab-panel">
            <div className="info-grid">
              <div className="info-card">
                <h3>Métriques de connexion</h3>
                <div className="info-row">
                  <span className="info-label">Dernière connexion :</span>
                  <span className="info-value">
                    {user.last_login_at ? formatDate(user.last_login_at) : 'Jamais'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Nombre de connexions :</span>
                  <span className="info-value">{user.login_count}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Dernière activité :</span>
                  <span className="info-value">
                    {user.last_activity_at ? formatDate(user.last_activity_at) : 'Aucune'}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Inscrit depuis :</span>
                  <span className="info-value">
                    {Math.floor((new Date().getTime() - new Date(user.created_at).getTime()) / (1000 * 60 * 60 * 24))} jours
                  </span>
                </div>
              </div>

              <div className="info-card">
                <h3>Activité sur la plateforme</h3>
                <div className="info-row">
                  <span className="info-label">Tableaux créés :</span>
                  <span className="info-value">{boards.length}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Tableaux actifs :</span>
                  <span className="info-value">{usage.owned_boards_count}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Invités gérés :</span>
                  <span className="info-value">
                    {usage.invited_editors_count + usage.invited_visitors_count}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Statut :</span>
                  <span className="info-value">
                    {user.last_activity_at && 
                     new Date(user.last_activity_at).getTime() > new Date().getTime() - 7 * 24 * 60 * 60 * 1000
                      ? '🟢 Actif'
                      : user.last_activity_at && 
                        new Date(user.last_activity_at).getTime() > new Date().getTime() - 30 * 24 * 60 * 60 * 1000
                      ? '🟡 Peu actif'
                      : '🔴 Inactif'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetail;
